import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import * as moment from 'moment';
import { Moment } from 'moment';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { first, map, mergeMap, publishReplay, refCount, switchMap, tap } from 'rxjs/operators';
import { deserialize } from 'santee-dcts';
import { Settings } from '../../core/settings';
import { AnnouncementInfo, AnnouncementsInfoReadModel } from './announcement-info';
import { ActivityLogger } from '../../activities/activity-logger';
import { comparators } from '../../core/comparators';

@Injectable()
export class AnnouncementsInfoStorage {
    private readonly apiPath = 'admin/announcements';
    private readonly announcementsInfo: Observable<AnnouncementInfo[]>;
    private readonly refresher = new BehaviorSubject<boolean>(true);
    constructor(
        private readonly http: HttpClient,
        private readonly settings: Settings,
        private readonly activityLogger: ActivityLogger,
    ) {
        this.announcementsInfo = this.loadAll()
            .pipe(
                publishReplay(1),
                refCount()
            );
    }

    public get all(): Observable<AnnouncementInfo[]> {
        return this.announcementsInfo;
    }

    public insert(announcement: AnnouncementRequest) {
        this.activityLogger.logActivity('Create Announcement');

        this.http.post(`${this.settings.apiUrl}/${this.apiPath}`, announcement)
            .pipe(
                tap(() => this.refresher.next(true)),
                first()
            ).toPromise();
    }

    public update(announcement: AnnouncementRequest) {
        this.activityLogger.logActivity('Edit Announcement');

        this.http.put(`${this.settings.apiUrl}/${this.apiPath}/${announcement.id}`, announcement)
            .pipe(
                tap(() => this.refresher.next(true)),
                first()
            ).toPromise();
    }

    public deleteAnnouncement(announcement: AnnouncementInfo, auditTrailReason: string) {
        this.activityLogger.logActivity('Delete Announcement');

        this.http.delete(`${this.settings.apiUrl}/${this.apiPath}/${announcement.id}?auditTrailReason=${auditTrailReason}`)
            .pipe(
                tap(() => this.refresher.next(true)),
                first()
            ).toPromise();
    }

    private loadAll(): Observable<AnnouncementInfo[] | null> {
        return this.refresher.pipe(
            mergeMap(() => of(of(null), this.http.get(`${this.settings.apiUrl}/${this.apiPath}`))),
            switchMap(x => x),
            map((rawData) => {
                if (rawData) {
                    let announcementsInfos = deserialize(rawData, AnnouncementsInfoReadModel).announcements;
                    announcementsInfos = announcementsInfos.sort((a, b) => comparators.dateTimeComparator(b ? b.posted : null, a ? a.posted : null));
                    return announcementsInfos;
                }

                return null;
            })
        );

    }
}

export class AnnouncementRequest {
    private static dateFormat = 'YYYY-MM-DD';

    public auditTrailReason: string;

    constructor(
        public readonly id: number,
        public readonly text: string,
        public readonly title: string,
        public readonly effectiveStartDate: Moment,
        public readonly effectiveEndDate: Moment,
        public readonly roles: string[],
        public readonly pinnedFlag: boolean
    ) { }

    public toJSON(): Object {
        const overrided: { [key in keyof AnnouncementInfo]?: any } = {
            effectiveStartDate: moment.isMoment(this.effectiveStartDate) && this.effectiveStartDate.isValid()
                ? this.effectiveStartDate.format(AnnouncementRequest.dateFormat)
                : null,
            effectiveEndDate: moment.isMoment(this.effectiveEndDate) && this.effectiveEndDate.isValid()
                ? this.effectiveEndDate.format(AnnouncementRequest.dateFormat)
                : null,
        };

        const serialized = Object.assign({}, this, overrided);

        return serialized;
    }
}
